import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styles from "./syllabus.module.css";
import AxiosInstance, { TokenService } from "../../../../services/Api";
// import { CustomDropdown } from "../../../../components";
import CustomMRTTable from "../../../../components/CustomMRTTable/CustomMRTTable";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import PopupModal from "../../../../components/PopupModal/PopupModal";

const SyllabuSubmissionList = () => {
  const user = TokenService.getUser();
  const clientData = user?.client_data[0] || {};
  const school_id = clientData?.school_id || "";
  const staffId =
    clientData.master_id != null && clientData.master_id !== ""
      ? Number(clientData.master_id)
      : null;

  const { translations } = useSelector((state) => state.language);
  const navigate = useNavigate();

  const [bookValue, setBookValue] = useState(null);
  const [bookOptions, setBookOptions] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [rawData, setRawData] = useState([]); // full dataset for the selected book
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [modalState, setModalState] = useState({
    open: false,
    type: "success",
    message: "",
    redirectPath: "",
  });

  // ───────────────────────────────────────────
  //  Refs holding the latest values so callbacks
  //  stay stable and don't re-trigger the API
  // ───────────────────────────────────────────
  const bookValueRef = useRef(null);
  const filtersRef = useRef({ topic: "", subTopic: "" });
  const hadBookRef = useRef(false); // becomes true once a real book is selected

  useEffect(() => {
    bookValueRef.current = bookValue;
  }, [bookValue]);

  // Keep the topic / sub_topic filter text in a ref (no API call here)
  useEffect(() => {
    filtersRef.current.topic =
      columnFilters.find((f) => f.id === "topic")?.value || "";
    filtersRef.current.subTopic =
      columnFilters.find((f) => f.id === "sub_topic")?.value || "";
  }, [columnFilters]);

  // ───────────────────────────────────────────
  // 1. Load staff books and auto-select first
  // ───────────────────────────────────────────
  const mapStaffBooksToOptions = (rows) =>
    rows.map((item) => ({
      label: item.book_name,
      value: item.info.id,
      info: item.info,
    }));

  const loadStaffBooks = useCallback(async () => {
    if (school_id == null || staffId == null) return;
    try {
      const res = await AxiosInstance.post("/book/getStaffBook", {
        school_id: school_id,
        staff_id: staffId,
      });
      if (res.data?.status && Array.isArray(res.data.data)) {
        const options = mapStaffBooksToOptions(res.data.data);
        setBookOptions(options);

        if (options.length > 0) {
          setBookValue(options[0]);
          // ✅ pre-select first book in the column filter dropdown
          setColumnFilters([{ id: "book", value: options[0].label }]);
        }
      }
    } catch (err) {
      console.error("Error loading staff books:", err);
    }
  }, [school_id, staffId]);

  useEffect(() => {
    loadStaffBooks();
  }, [loadStaffBooks]);

  // ───────────────────────────────────────────
  // 2. Fetch topics (server-side topic / sub_topic filtering).
  //    Stable: depends only on school_id, so it never re-fires on its own.
  //    bookId may be "" (when the book selection is cleared).
  // ───────────────────────────────────────────
  const fetchTopics = useCallback(
    async ({ bookId = "", topic = "", subTopic = "" }) => {
      if (!school_id) return;

      setLoading(true);
      try {
        const payload = {
          school_id: Number(school_id),
          id: bookId || "",
          topic: topic,
          sub_topic: subTopic,
          completed_date: 1,
        };
        const res = await AxiosInstance.post("/book/getBookTopic", payload);
        if (res.data?.status && Array.isArray(res.data.data)) {
          setRawData(res.data.data);
        } else {
          setRawData([]);
        }
      } catch (err) {
        console.error("Error loading book topics:", err);
        setRawData([]);
      } finally {
        // Always jump back to the first page on a fresh result set
        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
        setLoading(false);
      }
    },
    [school_id],
  );

  // ───────────────────────────────────────────
  // 3. Sync the Book column-filter dropdown → bookValue.
  //    Handles both selecting a book and clearing it.
  //    (does NOT call the API directly)
  // ───────────────────────────────────────────
  useEffect(() => {
    const bookFilter = columnFilters.find((f) => f.id === "book");

    // Book cleared (filter removed or emptied)
    if (!bookFilter || !bookFilter.value) {
      if (bookValue) setBookValue(null);
      return;
    }

    const matched = bookOptions.find((b) => b.label === bookFilter.value);
    if (matched && matched.value !== bookValue?.value) {
      setBookValue(matched);
    }
  }, [columnFilters, bookOptions, bookValue]);

  // ───────────────────────────────────────────
  // 4. Fetch exactly once whenever the selected book changes.
  //    - book selected   → fetch with that id
  //    - book cleared     → fetch with id: "" (only if a book was selected before)
  //    - initial mount    → no call (waits for the auto-selected first book)
  // ───────────────────────────────────────────
  useEffect(() => {
    if (bookValue?.value) {
      fetchTopics({
        bookId: bookValue.value,
        topic: filtersRef.current.topic,
        subTopic: filtersRef.current.subTopic,
      });
      hadBookRef.current = true;
    } else if (hadBookRef.current) {
      // a previously selected book was cleared
      fetchTopics({
        bookId: "",
        topic: filtersRef.current.topic,
        subTopic: filtersRef.current.subTopic,
      });
      hadBookRef.current = false;
    }
  }, [bookValue, fetchTopics]);

  // ───────────────────────────────────────────
  // 5. Enter-to-search for Topic / Sub Topic columns.
  //    Stable callback (refs hold the live values).
  // ───────────────────────────────────────────
  const handleSearchEnter = useCallback(
    (field) => (e) => {
      if (e.key !== "Enter") return;

      const value = e.target.value || "";
      const topic = field === "topic" ? value : filtersRef.current.topic;
      const subTopic =
        field === "sub_topic" ? value : filtersRef.current.subTopic;

      // keep the ref in sync immediately
      filtersRef.current.topic = topic;
      filtersRef.current.subTopic = subTopic;

      const bookId = bookValueRef.current?.value;
      if (bookId) {
        fetchTopics({ bookId, topic, subTopic });
      }
    },
    [fetchTopics],
  );

  // ───────────────────────────────────────────
  // 6. Derived table data (map fallbacks + client-side paging)
  // ───────────────────────────────────────────
  const processedData = useMemo(
    () =>
      (rawData || []).map((item) => ({
        ...item,
        book: bookValue?.label || "—",
        topic: item.topic || "—",
        sub_topic: item.sub_topic || "—",
        month: item.month || "—",
      })),
    [rawData, bookValue],
  );

  const rowCount = processedData.length;

  const pagedData = useMemo(() => {
    const start = pagination.pageIndex * pagination.pageSize;
    return processedData.slice(start, start + pagination.pageSize);
  }, [processedData, pagination.pageIndex, pagination.pageSize]);

  // ───────────────────────────────────────────
  // 7. Columns
  // ───────────────────────────────────────────
  const columns = useMemo(
    () => [
      {
        accessorKey: "sno",
        header: translations?.labels?.serialNo || "S.No",
        enableSorting: false,
        enableColumnFilter: false,
        size: 80,
        // data is the current page slice → add the page offset for a global serial
        Cell: ({ row }) =>
          row.index + 1 + pagination.pageIndex * pagination.pageSize,
      },
      {
        accessorKey: "book",
        header: translations?.labels?.book || "Book",
        enableSorting: false,
        filterVariant: "select",
        filterSelectOptions: bookOptions.map((b) => ({
          text: b.label,
          value: b.label,
        })),
        muiFilterTextFieldProps: {
          placeholder: "Select book",
        },
      },
      {
        accessorKey: "topic",
        header: translations?.labels?.topic || "Topic",
        enableSorting: false,
        muiFilterTextFieldProps: {
          placeholder: "Type & press Enter",
          onKeyDown: handleSearchEnter("topic"),
        },
      },
      {
        accessorKey: "sub_topic",
        header: translations?.labels?.subTopic || "Sub Topic",
        enableSorting: false,
        muiFilterTextFieldProps: {
          placeholder: "Type & press Enter",
          onKeyDown: handleSearchEnter("sub_topic"),
        },
      },
      {
        accessorKey: "month",
        header: translations?.labels?.completionMonth || "Completion Month",
        enableSorting: false,
        enableColumnFilter: false, // ✅ no filter on Completion Month
        Cell: ({ cell }) => cell.getValue() || "—",
      },
    ],
    [
      translations,
      pagination.pageIndex,
      pagination.pageSize,
      bookOptions,
      handleSearchEnter,
    ],
  );

  const handleSuccessOk = () => {
    const { type, redirectPath } = modalState;
    setModalState((prev) => ({ ...prev, open: false }));
    if (type === "success" && redirectPath) {
      navigate(redirectPath);
    }
  };

  return (
    <>
      <PopupModal
        open={modalState.open}
        title={modalState.type === "success" ? "Success" : "Failed"}
        message={modalState.message}
        onOk={handleSuccessOk}
      />
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <h1 className={styles.headerTitle}>
            {translations?.labels?.syllabusList || "Syllabus List"}
          </h1>
        </div>

        {/* Table */}
        <CustomMRTTable
          columns={columns}
          data={pagedData}
          loading={loading}
          pagination={pagination}
          rowCount={rowCount}
          onPaginationChange={setPagination}
          manualPagination={true} // we slice the data ourselves
          manualFiltering={true} // topic / sub_topic filtered via API
          columnFilters={columnFilters}
          onColumnFiltersChange={setColumnFilters}
          enableFiltering={true}
          enableSearch={false}
          localization={{
            noRecordsToDisplay:
              translations?.labels?.noRecordsFound || "No results found",
          }}
        />
      </div>
    </>
  );
};

export default SyllabuSubmissionList;
